import React, { useState, useRef, useEffect } from 'react';
import { Card, Button } from 'react-bootstrap';
import './AudioPlayerCard.css';

const AudioPlayerCard: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Aggiorna il tempo corrente
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
    };
  }, []);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleSkipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.currentTime + 30,
        duration
      );
    }
  };

  const handleSkipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        audioRef.current.currentTime - 15,
        0
      );
    }
  };

  const handleDownloadBonusTrack = async () => {
    const link = document.createElement('a');
    link.href = '/audio/Voce-Meditazione-dei-7-colori.mp3';
    link.download = 'Voce-Meditazione-dei-7-colori.mp3';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Card className="audio-player-card border-0 shadow-sm">
      <Card.Body className="p-4">
        <h3 className="fw-bold mb-4 text-center">
          RICARICATI DI ENERGIA IN SOLI 7 MINUTI
        </h3>

        <div className="audio-controls-modern mb-4">
          {/* Timer e Progress Bar */}
          <div className="progress-section mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="time-current text-muted">
                {formatTime(currentTime)}
              </small>
              <small className="time-duration text-muted">
                {formatTime(duration)}
              </small>
            </div>
            <div className="progress-bar-container">
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                className="modern-progress-slider"
                style={{ '--progress': `${progress}%` } as React.CSSProperties}
                disabled={!audioRef.current}
              />
            </div>
          </div>

          {/* Controlli principali */}
          <div className="main-controls d-flex align-items-center justify-content-center gap-4">
            {/* Skip Backward */}
            <Button
              variant="link"
              className="control-btn p-0"
              onClick={handleSkipBackward}
              disabled={!audioRef.current}
              title="Indietro 15 secondi"
            >
              <div className="control-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
                  <path d="M10.89 16h-.85v-3.26l-1.01.31v-.69l1.77-.63h.09V16z" />
                </svg>
                <small className="control-label">15s</small>
              </div>
            </Button>

            {/* Play/Pause */}
            <Button
              variant="primary"
              className="play-pause-btn rounded-circle"
              onClick={handlePlayPause}
              disabled={!audioRef.current}
              style={{ width: '70px', height: '70px' }}
            >
              {isPlaying ? (
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              ) : (
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </Button>

            {/* Skip Forward */}
            <Button
              variant="link"
              className="control-btn p-0"
              onClick={handleSkipForward}
              disabled={!audioRef.current}
              title="Avanti 30 secondi"
            >
              <div className="control-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12.01 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z" />
                  <path d="M13.11 16h.85v-3.26l1.01.31v-.69l-1.77-.63h-.09V16z" />
                </svg>
                <small className="control-label">30s</small>
              </div>
            </Button>
          </div>

          {/* Info traccia */}
          <div className="audio-info text-center mb-4">
            <div className="fw-bold">Traccia Audio Bonus</div>
            <small className="text-muted">
              Visualizzazione guidata • 7 minuti
            </small>
          </div>
        </div>

        {/* Azioni */}
        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
          <Button
            variant="secondary"
            onClick={handlePlayPause}
            className="d-flex align-items-center justify-content-center gap-2 flex-fill"
            disabled={!audioRef.current}
          >
            {isPlaying ? '⏸' : '▶'}{' '}
            {isPlaying ? 'IN RIPRODUZIONE' : 'ASCOLTA ORA'}
          </Button>

          <Button
            variant="outline-secondary"
            onClick={handleDownloadBonusTrack}
            className="d-flex align-items-center justify-content-center gap-2 flex-fill"
          >
            📥 SCARICA
          </Button>
        </div>

        {/* Audio element */}
        <audio
          ref={audioRef}
          onEnded={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          preload="metadata"
        >
          <source
            src="/audio/Voce-Meditazione-dei-7-colori.mp3"
            type="audio/mpeg"
          />
          Il tuo browser non supporta l'elemento audio.
        </audio>
      </Card.Body>
    </Card>
  );
};
export default AudioPlayerCard;
